import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { BrnDialogDescription, BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { IconComponent } from '@lite-workspace/shared/blocks/icon';

export interface RentTemplateDialogContext {
  packName: string;
  priceRub: number | null;
  studioName: string;
  studioTg: string;
  /** Ссылка на чат студии: её открывает САМ клик по «Написать студии», а не продолжение закрытия —
   *  Spartan закрывает диалог через анимацию, и к тому моменту жест уже потерян. */
  chatUrl: string;
}

const rub = new Intl.NumberFormat('ru-RU');

/** The rental fallback: a studio without a shop invoices by hand, so the deal starts as a Telegram message. */
@Component({
  selector: 'app-rent-template-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, DialogSheetComponent, IconComponent, BrnDialogTitle, BrnDialogDescription],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 brnDialogTitle class="mb-3 text-lg font-bold leading-snug text-ink">{{ title }}</h2>
        <p brnDialogDescription class="text-base leading-relaxed text-ink">
          Напишите студии — она выставит счёт. После оплаты шаблон добавится сам.
        </p>
        <div class="mt-4.5 flex items-center gap-3 rounded-[var(--r)] border border-line bg-bg2 px-3.5 py-3">
          <span class="grid size-8.5 shrink-0 place-items-center rounded-full bg-sky text-on-media">
            <app-icon name="send" [size]="18" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-bold text-ink">{{ ctx.studioName }}</span>
            <span class="block truncate text-xs text-c3">{{ ctx.studioTg }}</span>
          </span>
          <button hlmBtn type="button" variant="ghost" size="icon-sm" aria-label="Скопировать" (click)="copyHandle()">
            <app-icon name="copy" [size]="16" />
          </button>
        </div>
        <div class="mt-7 flex gap-3">
          <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="cancel()">Отмена</button>
          <button hlmBtn type="button" class="h-12 flex-1 justify-center gap-2" (click)="confirm()">
            <app-icon name="send" [size]="16" />
            Написать студии
          </button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class RentTemplateDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('xs', 'p-9');
  protected readonly ctx = injectBrnDialogContext<RentTemplateDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);
  private readonly clipboard = inject(Clipboard);
  private readonly toast = inject(ToastService);

  protected readonly title =
    this.ctx.priceRub === null
      ? `Аренда «${this.ctx.packName}»`
      : `Аренда «${this.ctx.packName}» — ${rub.format(this.ctx.priceRub)} ₽`;

  // Native clipboard first — the CDK execCommand fallback copies nothing inside the dialog's focus trap.
  protected async copyHandle(): Promise<void> {
    let copied = false;
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(this.ctx.studioTg);
        copied = true;
      } catch {
        copied = false;
      }
    }

    if (!copied) copied = this.clipboard.copy(this.ctx.studioTg);
    if (copied) this.toast.success('Скопировано');
    else this.toast.error('Не удалось скопировать — скопируйте вручную');
  }

  protected confirm(): void {
    if (this.ctx.chatUrl) window.open(this.ctx.chatUrl, '_blank', 'noopener');
    this.dialogRef.close(true);
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }
}
